import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, ClipboardText, CaretRight, CaretDown, Trash, CheckCircle, Circle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const STATUS_STYLES = {
  open: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  pending_validation: 'bg-yellow-100 text-yellow-700',
  closed: 'bg-green-100 text-green-700',
};

const DISCIPLINES = [
  { num: 1, name: 'D1: Team Formation', fields: [
    { key: 'd1_team_leader', label: 'Team Leader', type: 'text' },
    { key: 'd1_team_members_text', label: 'Team Members (comma-separated)', type: 'text' },
  ]},
  { num: 2, name: 'D2: Problem Description', fields: [
    { key: 'd2_problem_description', label: 'Problem Description', type: 'textarea' },
    { key: 'd2_is_is_not', label: 'IS / IS NOT Analysis', type: 'textarea' },
  ]},
  { num: 3, name: 'D3: Interim Containment Actions', fields: [
    { key: 'd3_containment_actions', label: 'Containment Actions', type: 'textarea' },
    { key: 'd3_verification', label: 'Verification', type: 'textarea' },
  ]},
  { num: 4, name: 'D4: Root Cause Analysis', fields: [
    { key: 'd4_root_cause', label: 'Root Cause', type: 'textarea' },
    { key: 'd4_five_why', label: '5-Why Analysis', type: 'textarea' },
    { key: 'd4_verification', label: 'Root Cause Verification', type: 'textarea' },
  ]},
  { num: 5, name: 'D5: Permanent Corrective Actions', fields: [
    { key: 'd5_corrective_actions', label: 'Corrective Actions', type: 'textarea' },
    { key: 'd5_verification', label: 'Verification', type: 'textarea' },
  ]},
  { num: 6, name: 'D6: Implementation & Validation', fields: [
    { key: 'd6_implementation', label: 'Implementation Details', type: 'textarea' },
    { key: 'd6_validation_results', label: 'Validation Results', type: 'textarea' },
  ]},
  { num: 7, name: 'D7: Preventive Actions', fields: [
    { key: 'd7_preventive_actions', label: 'Preventive Actions', type: 'textarea' },
    { key: 'd7_standardization', label: 'Standardization / Systemic Changes', type: 'textarea' },
  ]},
  { num: 8, name: 'D8: Team Recognition & Closure', fields: [
    { key: 'd8_lessons_learned', label: 'Lessons Learned', type: 'textarea' },
    { key: 'd8_team_recognition', label: 'Team Recognition', type: 'textarea' },
  ]},
];

export default function EightDReportsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'qa_manager' || user?.access_level === 'edit';
  const [reports, setReports] = useState([]);
  const [defects, setDefects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedReport, setExpandedReport] = useState(null);
  const [expandedDiscipline, setExpandedDiscipline] = useState(null);
  const [disciplineData, setDisciplineData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rRes, dRes, pRes, cRes] = await Promise.all([
        api.get('/8d-reports'),
        api.get('/defects').catch(() => ({ data: [] })),
        api.get('/apqp/projects').catch(() => ({ data: [] })),
        api.get('/complaints').catch(() => ({ data: [] })),
      ]);
      setReports(rRes.data);
      setDefects(dRes.data);
      setProjects(pRes.data);
      setComplaints(cRes.data);
    } catch (e) {
      toast.error('Failed to load 8D reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post('/8d-reports', {
        title: fd.get('title'),
        linked_defect_id: fd.get('linked_defect_id') || null,
        linked_project_id: fd.get('linked_project_id') || null,
        linked_complaint_id: fd.get('linked_complaint_id') || null,
      });
      toast.success('8D Report created');
      setShowCreateModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create report');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this 8D report?')) return;
    try {
      await api.delete(`/8d-reports/${id}`);
      toast.success('Report deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEditDiscipline = (report, num) => {
    const initial = {};
    DISCIPLINES.find(d => d.num === num).fields.forEach(f => {
      if (f.key === 'd1_team_members_text') {
        initial[f.key] = (report.d1_team_members || []).join(', ');
      } else {
        initial[f.key] = report[f.key] || '';
      }
    });
    initial[`d${num}_completed`] = report[`d${num}_completed`] || false;
    setDisciplineData(initial);
    setExpandedDiscipline(num);
  };

  const saveDiscipline = async (reportId, num) => {
    const payload = { ...disciplineData };
    if (num === 1 && payload.d1_team_members_text !== undefined) {
      payload.d1_team_members = payload.d1_team_members_text
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      delete payload.d1_team_members_text;
    }
    try {
      await api.patch(`/8d-reports/${reportId}/discipline/${num}`, payload);
      toast.success(`D${num} saved`);
      setExpandedDiscipline(null);
      setDisciplineData({});
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const getProgress = (report) => {
    const done = DISCIPLINES.filter(d => report[`d${d.num}_completed`]).length;
    return { done, total: 8, pct: (done / 8) * 100 };
  };

  const linkedLabel = (report) => {
    const parts = [];
    if (report.linked_defect_id) {
      const d = defects.find(x => x.id === report.linked_defect_id);
      if (d) parts.push(`Defect: ${d.defect_type}`);
    }
    if (report.linked_project_id) {
      const p = projects.find(x => x.id === report.linked_project_id);
      if (p) parts.push(`Project: ${p.project_name}`);
    }
    if (report.linked_complaint_id) {
      const c = complaints.find(x => x.id === report.linked_complaint_id);
      if (c) parts.push(`Complaint: ${c.complaint_number}`);
    }
    return parts;
  };

  return (
    <div data-testid="eightd-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">8D Reports</h1>
          <p className="mt-1 text-sm text-gray-600">Eight Disciplines problem-solving workflow</p>
        </div>
        {canEdit && (
          <button
            data-testid="create-8d-button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
          >
            <Plus size={20} weight="bold" />
            New 8D Report
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading 8D reports...</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <ClipboardText size={40} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">No 8D reports yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report, idx) => {
            const isExpanded = expandedReport === report.id;
            const progress = getProgress(report);
            const links = linkedLabel(report);
            return (
              <div
                key={report.id}
                data-testid={`eightd-card-${idx}`}
                className="bg-white border border-gray-200 rounded-sm"
              >
                <div
                  onClick={() => setExpandedReport(isExpanded ? null : report.id)}
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        {isExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
                        <span className="text-xs font-mono px-2 py-1 bg-gray-100 text-gray-700 rounded-sm">
                          {report.report_number}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">{report.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-sm ${STATUS_STYLES[report.status] || 'bg-gray-100 text-gray-700'}`}>
                          {report.status?.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      {links.length > 0 && (
                        <div className="mt-2 ml-7 flex flex-wrap gap-2 text-xs text-gray-600">
                          {links.map((l, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-sm">{l}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 ml-7">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 max-w-xs bg-gray-200 h-2 rounded-sm overflow-hidden">
                            <div className="bg-green-600 h-full transition-all" style={{ width: `${progress.pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-600">{progress.done} / 8 complete</span>
                        </div>
                      </div>
                    </div>
                    {canEdit && (user?.role === 'admin' || user?.role === 'qa_manager') && (
                      <button
                        data-testid={`delete-8d-${idx}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(report.id); }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-sm"
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-200 p-6 space-y-3">
                    {DISCIPLINES.map((disc) => {
                      const completed = report[`d${disc.num}_completed`];
                      const isEditingThis = expandedDiscipline === disc.num && expandedReport === report.id;
                      return (
                        <div key={disc.num} className="border border-gray-200 rounded-sm">
                          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3">
                              {completed ? (
                                <CheckCircle size={22} weight="fill" className="text-green-600" />
                              ) : (
                                <Circle size={22} className="text-gray-400" />
                              )}
                              <h4 className="font-semibold text-gray-900">{disc.name}</h4>
                              {report[`d${disc.num}_date`] && (
                                <span className="text-xs text-gray-500">
                                  {new Date(report[`d${disc.num}_date`]).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {canEdit && (
                              <button
                                data-testid={`edit-discipline-${disc.num}-report-${idx}`}
                                onClick={() => isEditingThis ? setExpandedDiscipline(null) : startEditDiscipline(report, disc.num)}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-sm hover:bg-gray-50"
                              >
                                {isEditingThis ? 'Cancel' : 'Edit'}
                              </button>
                            )}
                          </div>
                          {isEditingThis && (
                            <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
                              {disc.fields.map((field) => (
                                <div key={field.key}>
                                  <label className="block text-xs font-medium text-gray-700 mb-1 uppercase">{field.label}</label>
                                  {field.type === 'textarea' ? (
                                    <textarea
                                      rows={3}
                                      value={disciplineData[field.key] || ''}
                                      onChange={(e) => setDisciplineData({ ...disciplineData, [field.key]: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                                      data-testid={`field-${field.key}`}
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={disciplineData[field.key] || ''}
                                      onChange={(e) => setDisciplineData({ ...disciplineData, [field.key]: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                                      data-testid={`field-${field.key}`}
                                    />
                                  )}
                                </div>
                              ))}
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={disciplineData[`d${disc.num}_completed`] || false}
                                  onChange={(e) => setDisciplineData({ ...disciplineData, [`d${disc.num}_completed`]: e.target.checked })}
                                  data-testid={`mark-complete-d${disc.num}`}
                                />
                                Mark D{disc.num} as completed
                              </label>
                              <button
                                onClick={() => saveDiscipline(report.id, disc.num)}
                                data-testid={`save-discipline-${disc.num}`}
                                className="px-4 py-2 bg-gray-900 text-white text-sm rounded-sm hover:bg-gray-800"
                              >
                                Save D{disc.num}
                              </button>
                            </div>
                          )}
                          {!isEditingThis && (
                            <div className="border-t border-gray-200 p-4 space-y-2 text-sm">
                              {disc.fields.map((field) => {
                                let val = report[field.key];
                                if (field.key === 'd1_team_members_text') {
                                  val = (report.d1_team_members || []).join(', ');
                                }
                                return val ? (
                                  <div key={field.key}>
                                    <p className="text-xs font-medium text-gray-500 uppercase">{field.label}</p>
                                    <p className="text-gray-800 whitespace-pre-wrap">{val}</p>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">New 8D Report</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded-sm">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">REPORT TITLE *</label>
                <input
                  name="title"
                  data-testid="8d-title"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">LINKED DEFECT (Optional)</label>
                <select
                  name="linked_defect_id"
                  data-testid="8d-defect"
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm"
                >
                  <option value="">— None —</option>
                  {defects.map(d => (
                    <option key={d.id} value={d.id}>{d.defect_type} ({d.severity})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">LINKED APQP PROJECT (Optional)</label>
                <select
                  name="linked_project_id"
                  data-testid="8d-project"
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm"
                >
                  <option value="">— None —</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_name} ({p.part_number})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">LINKED COMPLAINT (Optional)</label>
                <select
                  name="linked_complaint_id"
                  data-testid="8d-complaint"
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm"
                >
                  <option value="">— None —</option>
                  {complaints.map(c => (
                    <option key={c.id} value={c.id}>{c.complaint_number} - {c.customer_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="8d-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
